import * as React from 'react';

const PAGE_SIZE = 50;

export interface DatasetEntry {
  id: number;
  parentId?: number | null;
}

export type DatasetPage = {
  pageIndex: number;
  topLevelIds: readonly number[];
};

export type DatasetPages = {
  pages: readonly DatasetPage[];
  pageIndexById: ReadonlyMap<number, number>;
  /**
   * Stable identifier for the page STRUCTURE (the top-level id sequence).
   * Becomes part of the page cache key so a filter/sort change — or a
   * top-level insert/delete — drops stale page data. It deliberately does NOT
   * change when a child is added or removed: the child is fetched incrementally
   * into its ancestor's existing page cache, instead of orphaning every page
   * (which would refetch and repaint all visible cells).
   */
  datasetSignature: string;
};

const EMPTY_PAGES: DatasetPages = {
  pages: [],
  pageIndexById: new Map(),
  datasetSignature: 'empty',
};

const findTopLevelAncestor = (
  id: number,
  parentById: ReadonlyMap<number, number | undefined>,
): number => {
  let current = id;
  while (true) {
    const parent = parentById.get(current);
    if (parent === undefined) return current;
    current = parent;
  }
};

export const useDatasetPages = (dataset?: readonly DatasetEntry[]): DatasetPages => {
  return React.useMemo(() => {
    if (!dataset || dataset.length === 0) return EMPTY_PAGES;

    const realEntries = dataset.filter((entry) => entry.id >= 0);
    if (realEntries.length === 0) return EMPTY_PAGES;

    const allIds = new Set<number>();
    for (const entry of realEntries) allIds.add(entry.id);

    const parentById = new Map<number, number | undefined>();
    const topLevelIds: number[] = [];
    for (const entry of realEntries) {
      const parentId = entry.parentId && allIds.has(entry.parentId) ? entry.parentId : undefined;
      parentById.set(entry.id, parentId);
      if (parentId === undefined) topLevelIds.push(entry.id);
    }

    const pages: DatasetPage[] = [];
    for (let i = 0; i < topLevelIds.length; i += PAGE_SIZE) {
      pages.push({
        pageIndex: pages.length,
        topLevelIds: topLevelIds.slice(i, i + PAGE_SIZE),
      });
    }

    // Every dataset id resolves to the page containing its top-level ancestor.
    // Children inherit their ancestor's page so expansion writes land in the
    // right cache entry.
    const topLevelToPage = new Map<number, number>();
    for (const page of pages) {
      for (const id of page.topLevelIds) topLevelToPage.set(id, page.pageIndex);
    }
    const pageIndexById = new Map<number, number>();
    for (const entry of realEntries) {
      const ancestor = findTopLevelAncestor(entry.id, parentById);
      const pageIndex = topLevelToPage.get(ancestor);
      if (pageIndex !== undefined) pageIndexById.set(entry.id, pageIndex);
    }

    // Order-sensitive hash of the top-level id sequence (child add/remove leaves
    // it untouched). An unsorted reorder reuses loaded rows via carryForwardPageCache.
    let hash = topLevelIds.length >>> 0;
    for (const id of topLevelIds) {
      hash = (hash * 31 + id) >>> 0;
    }
    const datasetSignature = hash.toString(36);

    return { pages, pageIndexById, datasetSignature };
  }, [dataset]);
};
